import React, { useState, useEffect, useRef } from 'react';
import { fetchRandomNotes } from '../../services/notesService';
import { Note } from '../../types/interfaces';
import '../../styles/FeaturedNotesCarousel.css';

const FeaturedNotesCarousel: React.FC = () => {
  const [currentNote, setCurrentNote] = useState(0);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(true);
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch random notes on component mount
  useEffect(() => {
    const loadRandomNotes = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const randomNotes = await fetchRandomNotes(10);
        setNotes(randomNotes);
      } catch (err) {
        console.error('Failed to load random notes:', err);
        setError('Unable to load featured notes at this time.');
      } finally {
        setIsLoading(false);
      }
    };

    loadRandomNotes();
  }, []); // Run only once on mount

  // Auto-advance functionality
  useEffect(() => {
    if (!isAutoAdvancing || !notes || notes.length === 0) return;

    intervalRef.current = setInterval(() => {
      setCurrentNote((prev) => (prev + 1) % notes.length);
    }, 30000); // Advance every 30 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoAdvancing, currentNote, notes]);

  // Reset auto-advance timer
  const resetTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isAutoAdvancing && notes && notes.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentNote((prev) => (prev + 1) % notes.length);
      }, 30000);
    }
  };

  const showNote = (index: number) => {
    setCurrentNote(index);
    resetTimer();
  };

  const handlePrevious = () => {
    if (!notes || notes.length === 0) return;
    const newIndex = (currentNote - 1 + notes.length) % notes.length;
    setCurrentNote(newIndex);
    resetTimer();
  };

  const handleNext = () => {
    if (!notes || notes.length === 0) return;
    const newIndex = (currentNote + 1) % notes.length;
    setCurrentNote(newIndex);
    resetTimer();
  };

  const handleMouseEnter = () => {
    setIsAutoAdvancing(false);
  };

  const handleMouseLeave = () => {
    setIsAutoAdvancing(true);
  };

  // Helper function to format location
  const formatLocation = (note: Note): string => {
    if (note.location_city && note.location_state) {
      return `${note.location_city}, ${note.location_state}`;
    }
    if (note.location_city) return note.location_city;
    if (note.location_state) return note.location_state;
    return 'Unknown';
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="featured-notes-section">
        <div className="container">
          <h2 className="featured-notes-heading">Featured Community Notes</h2>
          <div className="notes-carousel">
            <div className="note-card active">
              <p className="note-text text-center">Loading featured notes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="featured-notes-section">
        <div className="container">
          <h2 className="featured-notes-heading">Featured Community Notes</h2>
          <div className="notes-carousel">
            <div className="note-card active">
              <p className="note-text text-center">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!notes || notes.length === 0) {
    return (
      <div className="featured-notes-section">
        <div className="container">
          <h2 className="featured-notes-heading">Featured Community Notes</h2>
          <div className="notes-carousel">
            <div className="note-card active">
              <p className="note-text text-center">No featured notes available at this time.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="featured-notes-section">
      <div className="container">
        <h2 className="featured-notes-heading">Featured Community Notes</h2>

        <div
          className="notes-carousel"
          ref={carouselRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {notes.map((note, index) => (
            <div
              key={note._id}
              className={`note-card ${index === currentNote ? 'active' : ''}`}
              data-note={index + 1}
            >
              <svg className="quote-icon" width="40" height="40" viewBox="0 0 40 40" fill="currentColor">
                <path d="M10 20c0-5 3-9 7-9v4c-2 0-3 2-3 5h3v10h-7V20zm13 0c0-5 3-9 7-9v4c-2 0-3 2-3 5h3v10h-7V20z"/>
              </svg>

              <p className="note-text">{note.note}</p>

              <div className="note-meta">
                <div className="note-stats">
                  {/* Only show heart count if greater than 0 */}
                  {note.heart_count > 0 && (
                    <span className="note-hearts">
                      <i className="fas fa-heart"></i> {note.heart_count}
                    </span>
                  )}
                  <span className="note-location">
                    <i className="fas fa-map-marker-alt"></i> {formatLocation(note)}
                  </span>
                </div>
                {note.contributorAliasName && (
                  <span className="note-author">— {note.contributorAliasName}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="carousel-navigation">
          <button
            className="carousel-nav-btn carousel-prev"
            onClick={handlePrevious}
            aria-label="Previous note"
            disabled={!notes || notes.length === 0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          <div className="carousel-dots">
            {notes.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentNote ? 'active' : ''}`}
                onClick={() => showNote(index)}
                aria-label={`Note ${index + 1}`}
              />
            ))}
          </div>

          <button
            className="carousel-nav-btn carousel-next"
            onClick={handleNext}
            aria-label="Next note"
            disabled={!notes || notes.length === 0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturedNotesCarousel;
